import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import { useConfirmationWizard } from '@/features/Quote/hooks/useConfirmationWizard';
import { cn } from '@/lib/utils';
import type { SettingType } from '@/types/app';
import Badge from './items/Badge';
import CheckMark from './items/CheckMark';
import Circle from './items/Circle';
import InsurerRating from './items/InsurerRating';

// Decimal places used when displaying the premium rate percentage. Kept in one
// place so the on-screen figures stay consistent across the card layouts.
const PREMIUM_RATE_DECIMALS = 3;

interface ItemProps {
	formId: string;
	quoteId: string;
	insurerSlug: string;
	insurerName: string;
	imageKey?: string;
	premiumRatePercentage: number | null;
	savingPercentage: number | null;
	savingAmount: number | null;
	isBestOffer?: boolean;
	isUnavailable?: boolean;
	selected?: boolean;
	complete?: boolean;
	isPolling?: boolean;
	openSetting?: (settingType: SettingType, fromQuoteSelection?: boolean) => void;
}

const Item = ({
	formId,
	quoteId,
	insurerSlug,
	insurerName,
	imageKey,
	premiumRatePercentage,
	savingPercentage,
	savingAmount,
	isBestOffer = false,
	isUnavailable = false,
	selected = false,
	complete = false,
	isPolling = false,
	openSetting
}: ItemProps) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState<boolean>();
	const [isSelected, setIsSelected] = useState(selected);
	const { confirmedSteps } = useAppContext();
	const { getNextGuidanceStep } = useConfirmationWizard();

	const navigate = useNavigate();

	const API_URL = import.meta.env.VITE_API_BASE_URL;

	// Check if premium rate is effectively zero or null (no valid rate data)
	// Zero-rate detection uses 4 decimals (independent of display precision) so a
	// tiny-but-real rate isn't misread as missing data.
	const isRateZeroOrNull =
		premiumRatePercentage === null || premiumRatePercentage?.toFixed(4) === '0.0000';
	// A real comparison exists when the backend returned numeric savings values.
	// `null` means no current-policy data to compare against; `0` means a real
	// comparison was made and the proposed premium matches the current one.
	const hasComparison = savingAmount != null && savingPercentage != null;
	const savingAmountRounded = savingAmount != null ? Math.round(savingAmount) : null;
	const isEqualPremium = hasComparison && savingAmountRounded === 0;
	const isCheaper = hasComparison && savingAmountRounded !== null && savingAmountRounded > 0;
	const isMoreExpensive = hasComparison && savingAmountRounded !== null && savingAmountRounded < 0;

	const getSavingsLabelKey = (): string => {
		if (isCheaper) return 'quotes.item.cheaper';
		if (isEqualPremium) return 'quotes.item.equalPremium';
		return 'quotes.item.higherPremium';
	};

	const handleSelect = () => {
		if (complete) return; // Don't allow selection when item is complete

		// If any warning triangle remains (employees, absence or coverage), open the
		// first still-outstanding step so the client is guided through the confirmation
		// wizard instead of navigating. Steps confirmed this session are skipped (same
		// rule as the top guidance bar), so Kiezen never reopens a step the user just
		// confirmed. Trusted sources ('client', 'initial_payload', and 'current_policy'
		// for coverage) are handled in the wizard's isStepPending.
		const firstPendingStep = getNextGuidanceStep(confirmedSteps);
		if (firstPendingStep && openSetting) {
			openSetting(firstPendingStep, true); // true indicates opened from quote selection
			return;
		}

		// Normal navigation behavior
		setIsLoading(true);
		setIsSelected(true);
		setTimeout(() => {
			navigate(`/aanvulling/${formId}/${quoteId}`);
		}, 500);
	};

	// Render savings text for mobile view
	const renderMobileSavingsText = () => {
		// Check isUnavailable FIRST - unavailable policies should always show "No savings possible"
		if (isUnavailable) {
			return <span className="text-muted-foreground">{t('quotes.item.noSavingsPossible')}</span>;
		}
		if (!hasComparison) {
			// Backend didn't return savings data: no current policy to compare against.
			return (
				<span className="text-muted-foreground">
					{isRateZeroOrNull ? t('quotes.item.noComparisonAvailable') : t('quotes.item.compareRate')}
				</span>
			);
		}
		if (isEqualPremium) {
			return <span className="text-muted-foreground">{t('quotes.item.samePremiumAsCurrent')}</span>;
		}
		if (isCheaper && savingAmount !== null) {
			return (
				<span>
					{t('quotes.item.basedOnCurrent')}
					<br className="sm:hidden" />
					<span className="hidden sm:inline"> </span>
					{t('quotes.item.youSave')}{' '}
					<span className="font-bold text-foreground">
						€ {Math.round(savingAmount).toLocaleString('nl-NL')}
					</span>{' '}
					{t('quotes.item.perYear')}
				</span>
			);
		}
		return (
			<span className="text-muted-foreground">
				{t('quotes.item.basedOnCurrent')}
				<br className="sm:hidden" />
				<span className="hidden sm:inline"> </span>
				{t('quotes.item.youPay')}{' '}
				<span className="font-bold text-foreground">
					€ {Math.round(Math.abs(savingAmount ?? 0)).toLocaleString('nl-NL')}
				</span>{' '}
				{t('quotes.item.morePerYear')}
			</span>
		);
	};

	return (
		<div data-testid="quote-item" className="relative mb-7 overflow-visible">
			<div
				className={cn(
					'bg-white rounded-xl border relative flex flex-col md:flex-row items-stretch overflow-visible',
					isSelected ? 'border-green-500' : 'border-border',
					!complete && 'cursor-pointer',
					(isPolling || isUnavailable) && 'pointer-events-none grayscale-75 opacity-50'
				)}
			>
				<CheckMark isSelected={isSelected} />
				<div
					className={cn(
						'w-full relative rounded-xl pt-4 md:pt-5',
						isSelected ? 'bg-green-50/70' : 'bg-white'
					)}
				>
					<div
						onClick={() => handleSelect()}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleSelect();
							}
						}}
						role="button"
						tabIndex={0}
						className={cn('w-full select-none', complete && 'pointer-events-none')}
						data-testid="quote-item-card"
					>
						{/* Desktop Layout (md and up) */}
						<div className="hidden md:flex flex-row items-stretch justify-center px-1">
							{/* Logo column */}
							<div className="flex flex-col items-center justify-center text-center w-full pt-7 pb-5 px-3 sm:px-10 lg:px-9 w-md-32 md:w-40 shrink-0">
								<img
									src={`${import.meta.env.VITE_API_BASE_URL}/assets/providers/${insurerSlug}/logo.png`}
									className="max-h-14 w-full object-contain mb-2"
									alt={`${insurerName} logo`}
								/>
								<InsurerRating imageKey={imageKey} insurerName={insurerName} className="mt-3" />
							</div>

							{/* Savings text column */}
							<div className="flex items-center justify-center lg:min-w-[285px] py-3 sm:py-0 md:flex-1 md:border-l border-border lg:px-6 2xl:px-8">
								{/* Check isUnavailable FIRST - unavailable policies always show "No savings possible" */}
								{isUnavailable ? (
									<p className="mb-0 text-center sm:text-left">
										<span className="text-muted-foreground whitespace-nowrap">
											{t('quotes.item.noSavingsPossible')}
										</span>
									</p>
								) : !hasComparison ? (
									<p className="mb-0">
										<span className="text-muted-foreground">
											{isRateZeroOrNull
												? t('quotes.item.noComparisonAvailable')
												: t('quotes.item.compareRate')}
										</span>
									</p>
								) : isEqualPremium ? (
									<p className="mb-0 text-center sm:text-left">
										<span className="text-muted-foreground">
											{t('quotes.item.samePremiumAsCurrent')}
										</span>
									</p>
								) : (
									<p className="mb-0 text-center sm:text-left">
										{isCheaper && savingAmount !== null ? (
											<span className="block">
												<span>
													<span className="whitespace-nowrap">
														{t('quotes.item.basedOnCurrent')}{' '}
													</span>
													<br />
													<span className="whitespace-nowrap">
														{t('quotes.item.youSave')}{' '}
														<span className="whitespace-nowrap font-bold text-foreground mx-1">
															€ {Math.round(savingAmount).toLocaleString('nl-NL')}
														</span>{' '}
														{t('quotes.item.perYear')}
													</span>
												</span>
											</span>
										) : (
											<span className="text-muted-foreground whitespace-nowrap">
												<span>
													<span className="whitespace-nowrap">
														{t('quotes.item.basedOnCurrent')}{' '}
													</span>
													<br />
													<span className="whitespace-nowrap">
														{t('quotes.item.youPay')}{' '}
														<span className="whitespace-nowrap font-bold text-foreground mx-1">
															€ {Math.round(Math.abs(savingAmount ?? 0)).toLocaleString('nl-NL')}
														</span>{' '}
														{t('quotes.item.morePerYear')}
													</span>
												</span>
											</span>
										)}
									</p>
								)}
							</div>

							{/* Premium rate column */}
							<div className="flex items-center justify-center py-3 sm:py-0 md:border-l border-border px-4 md:px-6 lg:px-6 2xl:px-8">
								{isRateZeroOrNull ? (
									<p className="mb-0 text-lg font-bold text-muted-foreground whitespace-nowrap text-center">
										...
									</p>
								) : (
									<div className="w-full text-center">
										<span
											className={cn(
												'mb-1 font-bold text-foreground whitespace-nowrap',
												hasComparison ? 'text-lg' : 'text-xl'
											)}
										>
											{premiumRatePercentage?.toFixed(PREMIUM_RATE_DECIMALS)}
										</span>
										<p className="mb-0 text-sm text-foreground whitespace-nowrap">
											{t('quotes.item.premiumRate')}
										</p>
									</div>
								)}
							</div>

							{/* Savings percentage column */}
							{hasComparison && savingPercentage !== null && (
								<div className="flex items-center justify-center py-3 sm:py-0 md:border-l border-border md:px-4 lg:px-6 2xl:px-8">
									<div className="w-full relative text-center leading-3">
										{isUnavailable ? (
											<p className="mb-0 text-lg font-bold text-muted-foreground whitespace-nowrap text-center">
												...
											</p>
										) : (
											<>
												{isCheaper ? (
													<Circle className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden sm:block pb-4 ml-7 mt-1" />
												) : (
													<span />
												)}
												<span>
													{isCheaper ? (
														<div className="pt-4">
															<span className="mb-1 text-6xl font-bold text-foreground whitespace-nowrap text-center">
																{savingPercentage.toFixed(0)}
																<span className="text-xl font-bold">%</span>
															</span>
															<p className="pt-2 mb-0 text-sm text-foreground whitespace-nowrap">
																{t('quotes.item.cheaper')}
															</p>
														</div>
													) : isEqualPremium ? (
														<div className="pt-4">
															<span className="mb-1 text-6xl font-bold text-foreground whitespace-nowrap text-center">
																0<span className="text-xl font-bold">%</span>
															</span>
															<p className="pt-2 mb-0 text-sm text-foreground whitespace-nowrap">
																{t('quotes.item.equalPremium')}
															</p>
														</div>
													) : (
														<div className="pt-4">
															<span className="mb-1 text-6xl font-bold text-foreground whitespace-nowrap text-center">
																{Math.abs(savingPercentage).toFixed(0)}
																<span className="text-xl font-bold">%</span>
															</span>
															<p className="pt-2 mb-0 text-sm text-foreground whitespace-nowrap">
																{t('quotes.item.higherPremium')}
															</p>
														</div>
													)}
												</span>
											</>
										)}
									</div>
								</div>
							)}

							{/* Button column */}
							<div
								className={cn(
									'items-center justify-center py-4 sm:py-0 md:border-l border-border px-6 lg:px-5 2xl:px-10 hidden md:flex',
									complete && 'hidden md:flex'
								)}
							>
								<Button
									type="button"
									variant={complete ? 'success' : 'default'}
									size="lg"
									className="min-w-[90px] mb-0 font-bold"
									onClick={handleSelect}
									disabled={(isLoading ?? isPolling) || complete}
									loading={isLoading ?? isPolling}
									data-testid="quote-item-select-button"
								>
									{complete ? t('quotes.item.selected') : t('quotes.item.select')}
								</Button>
							</div>
						</div>

						{/* Mobile Layout (xs/sm) */}
						<div className="md:hidden px-5 pb-4">
							{/* Header row: Logo + Rating + Percentage */}
							<div className="flex items-center justify-between pl-1 md:pl-0 pt-1 md:pt-4">
								{/* Logo and rating */}
								<div className="flex items-center gap-4">
									<img
										src={`${import.meta.env.VITE_API_BASE_URL}/assets/providers/${insurerSlug}/logo.png`}
										className="h-12 w-16 object-contain"
										alt={`${insurerName} logo`}
									/>
									<InsurerRating imageKey={imageKey} insurerName={insurerName} className="ml-1" />
								</div>
								{/* Percentage */}
								{hasComparison && savingPercentage !== null && !isUnavailable && (
									<div className="text-right flex flex-col items-end">
										<span className="text-3xl sm:text-4xl font-bold text-foreground">
											{Math.abs(savingPercentage).toFixed(0)}%
										</span>
										<p className="text-sm text-primary mb-0 text-right">
											{t(getSavingsLabelKey())}
										</p>
									</div>
								)}
							</div>

							{/* Checkmark list */}
							<div className="mt-2 space-y-2">
								{/* Premium rate row */}
								{!isRateZeroOrNull && (
									<div className="flex items-start gap-2">
										<svg
											className="w-5 h-5 text-primary shrink-0 mt-0.5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M5 13l4 4L19 7"
											/>
										</svg>
										<span className="text-foreground">
											{premiumRatePercentage?.toFixed(PREMIUM_RATE_DECIMALS)}{' '}
											{t('quotes.item.premiumRate')}
										</span>
									</div>
								)}
								{/* Savings row */}
								{renderMobileSavingsText() && (
									<div className="flex items-start gap-2">
										<svg
											className="w-5 h-5 text-primary shrink-0 mt-0.5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M5 13l4 4L19 7"
											/>
										</svg>
										<span className="text-foreground">{renderMobileSavingsText()}</span>
									</div>
								)}
							</div>

							{/* Mobile button */}
							<div className="mt-5">
								<Button
									type="button"
									variant={complete ? 'success' : 'default'}
									size="lg"
									className="w-full font-bold"
									onClick={handleSelect}
									disabled={(isLoading ?? isPolling) || complete}
									loading={isLoading ?? isPolling}
									data-testid="quote-item-select-button-mobile"
								>
									{complete ? t('quotes.item.selected') : t('quotes.item.select')}
								</Button>
							</div>
						</div>
					</div>

					{/* Footer with links */}
					<div
						className={cn(
							'rounded-b-xl px-4 py-2 mt-4 border-t border-border',
							!complete && 'bg-muted/50'
						)}
					>
						<div className="flex justify-center gap-6 md:justify-between md:gap-0">
							<a
								href={`${API_URL}/documents/${insurerSlug}/sick_leave_short_term/card`}
								rel="noopener noreferrer"
								className="text-foreground text-sm underline opacity-75 hover:opacity-100"
								data-testid="quote-item-card-link"
							>
								<FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs mr-2" />
								{t('quotes.item.insuranceCard')}
							</a>
							<a
								href={`${API_URL}/documents/${insurerSlug}/sick_leave_short_term/terms`}
								rel="noopener noreferrer"
								className="text-foreground text-sm underline opacity-75 hover:opacity-100"
								data-testid="quote-item-terms-link"
							>
								<FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs mr-2" />
								{t('quotes.item.terms')}
							</a>
						</div>
					</div>

					{/* Badges */}
					<Badge
						text={t('quotes.item.bestSavings')}
						signal="success"
						visible={isBestOffer && isCheaper}
					/>
					<Badge text={t('quotes.item.notAvailable')} signal="warning" visible={isUnavailable} />
					<Badge
						text={t('quotes.item.higherPremium')}
						signal="secondary"
						visible={isMoreExpensive}
					/>
				</div>
			</div>
			{/* <ModalToelichting /> */}
		</div>
	);
};

export default Item;
